import { useRef, useState } from 'react';
import { Paperclip, Send, X } from 'lucide-react';
import Mascot from '../components/Mascot';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { TYPES } from '../lib/constants';
import { submitPublicTicket } from '../lib/api';

const EMPTY = { name: '', email: '', title: '', description: '', type: 'question' };

function Shell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-xl">
        {children}
        <p className="mt-6 text-[11px] text-muted-foreground/60">
          powered by <span className="text-primary">~/ticky</span>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

// Public, login-free request form. Anyone with the link can file a ticket; they
// never see the board or any internal data.
export default function SubmitTicket() {
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [target, setTarget] = useState(null);
  const [wave, setWave] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Tell the mascot to look at whichever field was just focused.
  const lookAt = (e) => {
    const r = e.target.getBoundingClientRect();
    setTarget({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  };

  const addImages = (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    setImages((p) => [...p, ...imgs].slice(0, 6));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Please add a short summary.'); return; }
    setBusy(true);
    setError('');
    try {
      await submitPublicTicket(form, images);
      setDone(true);
      setWave((w) => w + 1);
    } catch {
      setError('Sorry — something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <div className="flex justify-center"><Mascot target={null} waveSignal={wave} className="h-24 w-24" /></div>
        <h1 className="mt-2 font-mono text-xl font-semibold">
          Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}! 🎉
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your request is in — the team will pick it up from here. You can close this tab.
        </p>
        <Button
          variant="outline"
          className="mt-5"
          onClick={() => { setDone(false); setForm((f) => ({ ...EMPTY, name: f.name, email: f.email })); setImages([]); setTarget(null); }}
        >
          Submit another
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex justify-center"><Mascot target={target} waveSignal={wave} className="h-24 w-24" /></div>
      <h1 className="mt-2 font-mono text-xl font-semibold">Send a request to the team</h1>
      <p className="mt-1 text-sm text-muted-foreground">Tell us what you need — no account required.</p>

      <form onSubmit={submit} className="mt-5 w-full space-y-3 text-left">
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Your name"><Input value={form.name} onChange={set('name')} onFocus={lookAt} placeholder="Jane Doe" /></Field>
          <Field label="Your email (optional)"><Input type="email" value={form.email} onChange={set('email')} onFocus={lookAt} placeholder="jane@company.com" /></Field>
        </div>

        <Field label="Summary *">
          <Input value={form.title} onChange={set('title')} onFocus={lookAt} placeholder="What do you need?" autoFocus />
        </Field>

        <Field label="Details">
          <Textarea value={form.description} onChange={set('description')} onFocus={lookAt} placeholder="Context, links, what happened…" />
        </Field>

        <Field label="Type">
          <Select value={form.type} onChange={set('type')} onFocus={lookAt}>
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>

        <Field label="Screenshots">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-sm text-muted-foreground transition hover:bg-accent"
          >
            <Paperclip className="h-4 w-4" /> Add images
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addImages(e.target.files); e.target.value = ''; }} />
          {images.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {images.map((f, i) => (
                <div key={i} className="relative h-14 w-14 overflow-hidden rounded border border-border">
                  <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setImages((p) => p.filter((_, j) => j !== i))} className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center bg-black/60 text-white" aria-label="Remove">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </Field>

        <Button type="submit" disabled={busy} className="w-full gap-1.5">
          <Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Send request'}
        </Button>
      </form>
    </Shell>
  );
}
